import type { HierarchyRectangularNode } from 'd3-hierarchy';

import { TOOLTIP_HOVER_TOLERANCE } from '../constants.js';
import type { InternalTreeMapNode } from '../types/internal.js';
import { nodePadding } from './node-padding.js';
import { shouldDrawLeafNode } from './should-draw-leaf-node.js';

type Position = { x: number; y: number };

/**
 * Squared distance from a position to the nearest point of a node's rectangle,
 * which is 0 for any position inside it. Squared to keep the comparisons
 * sqrt-free — this runs for every node on every pointer move.
 */
const squaredDistanceToNode = (
  node: HierarchyRectangularNode<InternalTreeMapNode>,
  position: Position,
) => {
  const dx = Math.max(node.x0 - position.x, 0, position.x - node.x1);
  const dy = Math.max(node.y0 - position.y, 0, position.y - node.y1);

  return Math.pow(dx, 2) + Math.pow(dy, 2);
};

/**
 * Resolves the node the pointer is over, or the nearest one within its hover
 * reach when the pointer sits in a gutter between nodes. Returns `undefined`
 * only when nothing is close enough, so the tooltip is dismissed when leaving
 * the treemap rather than when crossing between two of its nodes.
 */
export const getTooltipData = (
  leafNodes: HierarchyRectangularNode<InternalTreeMapNode>[],
  clusterNodes: HierarchyRectangularNode<InternalTreeMapNode>[],
  relativePosition: Position,
  hasParentLabels: boolean = false,
) => {
  const nodes = hasParentLabels ? [...leafNodes, ...clusterNodes] : leafNodes;

  let closestNode: HierarchyRectangularNode<InternalTreeMapNode> | undefined;
  let closestDistance = Infinity;

  nodes.forEach((node) => {
    if (hasParentLabels && !shouldDrawLeafNode(node)) {
      return;
    }

    const reach = nodePadding(node, hasParentLabels) + TOOLTIP_HOVER_TOLERANCE;
    const distance = squaredDistanceToNode(node, relativePosition);

    if (distance <= Math.pow(reach, 2) && distance < closestDistance) {
      closestNode = node;
      closestDistance = distance;
    }
  });

  return closestNode;
};
